import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { STORAGE_KEYS } from '../config/config';

export type ThemeMode = 'light' | 'dark';

interface ThemeModeContextValue {
  /** Active color scheme for the app shell. */
  mode: ThemeMode;
  /** Flip between light and dark; the choice is persisted and wins over OS preference. */
  toggle: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error('useThemeMode must be used within a ThemeModeProvider');
  return ctx;
}

/**
 * Resolve the initial mode: an explicit stored user choice wins; otherwise fall
 * back to the OS `prefers-color-scheme` setting; otherwise light. The OS value
 * is read once at init only — there is no live listener, so an OS change never
 * flips the app mid-session (an explicit toggle is stored and wins on next load).
 */
function getInitialMode(): ThemeMode {
  const stored = localStorage.getItem(STORAGE_KEYS.THEME_MODE);
  if (stored === 'light' || stored === 'dark') return stored;
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode);

  // Sync the custom-CSS token layer: tokens.css exposes a [data-theme="dark"]
  // block that overrides the semantic --color-* variables. Native Spectrum
  // components adapt separately via the Provider's colorScheme prop.
  useEffect(() => {
    document.documentElement.dataset.theme = mode;
  }, [mode]);

  const toggle = useCallback(() => {
    setMode((current) => {
      const next: ThemeMode = current === 'dark' ? 'light' : 'dark';
      localStorage.setItem(STORAGE_KEYS.THEME_MODE, next);
      return next;
    });
  }, []);

  const value = useMemo<ThemeModeContextValue>(() => ({ mode, toggle }), [mode, toggle]);

  return <ThemeModeContext.Provider value={value}>{children}</ThemeModeContext.Provider>;
};
